// src/components/TotalPago.tsx

// 🔹 Importaciones principales
import { useState, useEffect } from 'react';
import DescuentosPromocionales from './DescuentosPromocionales'; // Componente para aplicar códigos de descuento
import { calcularTotalConIVA } from '../../../utils/calcularTotalIva'; // Función utilitaria para calcular IVA
import { TotalPagoProps } from '@/types'; // Tipado de props

// 🔹 Componente principal
const TotalPago: React.FC<TotalPagoProps> = ({ precioOriginal, onTotalPagarChange, onDescuentoChange, setCodigoPromocional, onMensajeChange, descuentoHabilitado = false,  profesionId }) => {
  // 🔹 Estado interno
  const [descuento, setDescuento] = useState<number>(0); // % de descuento aplicado
  const [mensajeCodigo, setMensajeCodigo] = useState<string | null>(null); // Mensaje de error del código
  const [, setPrecioConDescuento] = useState<number>(precioOriginal ?? 0); // Precio después del descuento
  const [iva] = useState<number>(21); // % IVA por defecto
  const [totalPagar, setTotalPagar] = useState<number>(precioOriginal ?? 0); // Total final con IVA
  const [montoIVA, setMontoIVA] = useState<number>(0); // Monto de IVA calculado

  // 🔹 Efecto que recalcula precios y total cada vez que cambian precioOriginal, descuento o IVA
  useEffect(() => {
    // 1️⃣ Aplicar descuento al precio original
    const precioConDesc = (precioOriginal ?? 0) * (1 - descuento / 100);

    // 2️⃣ Calcular total con IVA usando la función utilitaria
    const totalConIVA = calcularTotalConIVA(precioConDesc, iva);

    // 3️⃣ Calcular solo el IVA aplicado
    const calculoIVA = totalConIVA - precioConDesc;

    // 4️⃣ Actualizar estados locales
    setPrecioConDescuento(precioConDesc);
    setMontoIVA(calculoIVA);
    setTotalPagar(totalConIVA);

    // 5️⃣ Ejecutar callback para informar al padre del total a pagar
    if (onTotalPagarChange) {
      onTotalPagarChange(totalConIVA);
    }
  }, [precioOriginal, descuento, iva, onTotalPagarChange]);

  // 🔹 Función que se pasa al componente de descuentos para actualizar el descuento
  const handleDescuentoChange = (nuevoDescuento: number): void => {
    setDescuento(nuevoDescuento);
    if (onDescuentoChange) {
      onDescuentoChange(nuevoDescuento); // Informar al componente padre
    }
  };

  const handleMensajeCodigo = (mensaje: string | null) => {
    setMensajeCodigo(mensaje);         // actualiza estado interno
    onMensajeChange?.(mensaje);        // envía al padre (Datos4)
  };


  return (
    <div>
      {/* 🔹 Componente de código promocional */}
      <DescuentosPromocionales
        descuento={descuento}
        onDescuentoChange={handleDescuentoChange}
        onMensajeChange={handleMensajeCodigo}
        onCodigoValido={setCodigoPromocional}
        descuentoHabilitado={descuentoHabilitado}
       profesionId={profesionId}
      />

      {/* 🔹 Mostrar resumen del descuento, IVA y total */}
      <div className="mt-2">
        {/* Mostrar descuento aplicado o mensaje de error */}
        <p
          className={`h-5 text-sm ${descuento > 0
              ? 'text-black' // Si hay descuento válido
              : mensajeCodigo
                ? 'text-red-500' // Si hay error en el código
                : 'text-black'
            }`}
        >
          {descuento > 0
            ? `Descuento: ${descuento.toString().replace(/\..*$/, '')}%` // Mostrar descuento sin decimales
            : mensajeCodigo || 'Descuento: 0%'}
        </p>

        {/* Mostrar IVA calculado */}
        <p>IVA ({iva}%): {montoIVA.toFixed(2)} €</p>

        {/* Mostrar total final a pagar */}
        <p>Total a pagar: {totalPagar.toFixed(2)} €</p>
      </div>
    </div>
  );
};

// 🔹 Exportación del componente
export default TotalPago;
